import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Package, FileText, Share2, ShoppingCart, BarChart3, Code, ArrowRight, CheckCircle2, Lightbulb, Rocket, BookOpen, Globe } from 'lucide-react';

const steps = [
  {
    number: 1,
    title: 'Create Your First Product',
    description: 'Upload your digital product (ebook, course, template, preset, or poster), set a price in MYR, and configure download limits and expiry duration.',
    icon: Package,
    color: 'bg-blue-100 text-blue-600',
    tutorialLink: '/app/getting-started/create-product',
    quickLink: '/app/products',
    quickLinkLabel: 'Go to Products',
    tips: [
      'Supported file types include PDF, ZIP, and other common formats',
      'Set download limits to control how many times a customer can download',
      'Add an expiry duration so download links expire automatically',
      'Upload a thumbnail image to make your product look professional'
    ]
  },
  {
    number: 2,
    title: 'Build Your Landing Page',
    description: 'Create a beautiful storefront page using our templates or custom editor. Add your products, customize the design, and add your social media links.',
    icon: FileText,
    color: 'bg-teal-100 text-teal-600',
    tutorialLink: '/app/getting-started/build-landing-page',
    quickLink: '/app/landing-pages',
    quickLinkLabel: 'Go to Landing Pages',
    tips: [
      'Choose from pre-made templates for a quick start',
      'Use the Custom editor for full control over your page layout',
      'Add Facebook Pixel or TikTok Pixel for ad tracking',
      'Customize colors, fonts, and product layout (List, Card, or Carousel)'
    ]
  },
  {
    number: 3,
    title: 'Publish & Share Your Link',
    description: 'Once your landing page is ready, publish it to get a unique URL. Share this link in your social media bio, WhatsApp, or anywhere your audience is.',
    icon: Share2,
    color: 'bg-amber-100 text-amber-600',
    tutorialLink: '/app/getting-started/publish-share',
    quickLink: '/app/landing-pages',
    quickLinkLabel: 'View My Pages',
    tips: [
      'Your published page is accessible at /store/your-slug',
      'Share the link on Instagram bio, Twitter, TikTok, and Facebook',
      'Use link shorteners for cleaner URLs if needed',
      'You can update your page anytime without changing the URL'
    ]
  },
  {
    number: 4,
    title: 'Manage Orders & Customers',
    description: 'When customers purchase your products, you can track every order, view customer details, reissue download links, or revoke access if needed.',
    icon: ShoppingCart,
    color: 'bg-emerald-100 text-emerald-600',
    tutorialLink: '/app/getting-started/manage-orders',
    quickLink: '/app/orders',
    quickLinkLabel: 'Go to Orders',
    tips: [
      'View order status (paid, pending) and payment details',
      'Reissue download links for customers who need new access',
      'Revoke access to stop downloads for refunded orders',
      'Check each customer profile for their complete purchase history'
    ]
  },
  {
    number: 5,
    title: 'Track Your Performance',
    description: 'Use the Sales Report and Overview dashboard to monitor revenue trends, top products, visitor analytics, and conversion rates by landing page.',
    icon: BarChart3,
    color: 'bg-rose-100 text-rose-600',
    tutorialLink: '/app/getting-started/track-performance',
    quickLink: '/app/sales-report',
    quickLinkLabel: 'View Sales Report',
    tips: [
      'Filter reports by date range (Today, Last 7 Days, Last Month, etc.)',
      'Track which landing pages convert the most visitors',
      'Monitor your top-selling products and revenue trends',
      'Use insights to optimize your pricing and marketing'
    ]
  },
  {
    number: 6,
    title: 'Embed Checkout Forms (Optional)',
    description: 'Want to sell from your own website or blog? Create an embeddable checkout form and add it to any external site with just 3 lines of code.',
    icon: Code,
    color: 'bg-violet-100 text-violet-600',
    tutorialLink: '/app/getting-started/embed-checkout',
    quickLink: '/app/checkout-forms',
    quickLinkLabel: 'Go to Checkout Forms',
    tips: [
      'Select which products to include in the checkout form',
      'Customize button text and form fields',
      'Copy the 3-line JavaScript snippet to your website',
      'The checkout form renders automatically on any website'
    ]
  },
  {
    number: 7,
    title: 'Connect Custom Domain (Premium)',
    description: 'Premium users can connect their own domain to their Funnely store. Point your domain to Funnely and your store will be accessible at your branded URL.',
    icon: Globe,
    color: 'bg-orange-100 text-orange-600',
    tutorialLink: '/app/getting-started/custom-domain',
    quickLink: '/app/profile',
    quickLinkLabel: 'Go to Profile',
    tips: [
      'Go to your domain registrar (Namecheap, GoDaddy, Cloudflare, etc.)',
      'Add a CNAME record: Host = your subdomain, Value = custom.funnely.co',
      'Save the record and wait up to 24 hours for DNS propagation',
      'Enter your domain in Profile > Custom Domain and click Save'
    ]
  },
];

export default function GettingStartedPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-8 max-w-[900px]" data-testid="getting-started-page">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Rocket className="h-6 w-6" style={{ color: '#FF9A00' }} />
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: '#195259' }}>Getting Started</h1>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-[600px]">
          Welcome to Funnely! Follow these steps to set up your digital product store and start selling to your audience.
        </p>
      </div>

      {/* Quick Overview Card */}
      <Card style={{ border: '1.5px solid #FF9A00' }}>
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(25,82,89,0.1)' }}>
              <Lightbulb className="h-5 w-5" style={{ color: '#FF9A00' }} />
            </div>
            <div>
              <p className="font-semibold text-sm" style={{ color: '#195259' }}>How Funnely Works</p>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                Funnely is a link-in-bio platform designed for Malaysian creators to sell digital products. 
                Upload your product, build a landing page, share the link, and get paid. It's that simple.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          return (
            <Card key={step.number} className="overflow-hidden" data-testid={`guide-step-${step.number}`}>
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  {/* Step indicator */}
                  <div className="md:w-16 flex items-center justify-center p-4 md:py-6 md:border-r shrink-0" style={{ backgroundColor: 'rgba(25,82,89,0.04)' }}>
                    <span className="text-2xl font-bold" style={{ color: '#195259', opacity: 0.3 }}>{step.number}</span>
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 p-5 md:p-6 space-y-4">
                    <div className="flex items-start gap-3">
                      <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${step.color}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base" style={{ color: '#195259' }}>{step.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{step.description}</p>
                      </div>
                    </div>

                    {/* Tips */}
                    <div className="pl-0 md:pl-[52px] space-y-2">
                      <Separator className="mb-3" />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {step.tips.map((tip, i) => (
                          <div key={i} className="flex items-start gap-2">
                            <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#FF9A00' }} />
                            <span className="text-xs text-muted-foreground leading-relaxed">{tip}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Action */}
                    <div className="pl-0 md:pl-[52px] flex items-center gap-2">
                      <Button
                        size="sm"
                        onClick={() => navigate(step.tutorialLink)}
                        data-testid={`guide-step-${step.number}-tutorial`}
                        style={{ backgroundColor: '#195259' }}
                        className="text-white"
                      >
                        <BookOpen className="h-3.5 w-3.5 mr-1.5" />
                        Read Full Guide
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(step.quickLink)}
                        data-testid={`guide-step-${step.number}-action`}
                      >
                        {step.quickLinkLabel}
                        <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Help Footer */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-sm" style={{ color: '#195259' }}>Need more help?</p>
              <p className="text-xs text-muted-foreground mt-1">
                Use the AI Support chat (bottom-right corner) to ask any questions about using Funnely.
              </p>
            </div>
            <Badge className="text-white" style={{ backgroundColor: '#FF9A00' }}>AI Support Available</Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
